import { useState } from "react";

import { Source } from "@/constants";
import { SourceDisplay } from "@components/DataDisplay";
import { Input } from "@components/Field";

export type MinimumReachByChannelType = Partial<
  Record<Source, number | string>
>;

export type MinimumReachControlProps = {
  id: string;
  name: Source;
  label: React.ReactNode;
  minReachByChannelType: MinimumReachByChannelType;
  onValueChange: (source: Source, value: number | string) => void;
};

type MinimumReachControlsProps = {
  className?: string;
  sourceOptions: Source[] | ReadonlyArray<Source>;
  minReachByChannelType: MinimumReachByChannelType;
  onChange: (next: MinimumReachByChannelType) => void;
};

export const MinimumReachControl = (props: MinimumReachControlProps) => {
  // Fallback to blank values if the (default) values are unavailable
  let initValue: string | number = "";

  if (
    !Object.prototype.hasOwnProperty.call(
      props.minReachByChannelType,
      "no_values"
    )
  ) {
    initValue = props.minReachByChannelType[props.name] || 0;
  }
  const [value, setValue] = useState(initValue);
  return (
    <div className="flex flex-col">
      <label htmlFor={props.id}>{props.label}</label>
      <Input
        block
        name={props.name}
        id={props.id}
        type="number"
        value={value}
        onChange={(e) => {
          const nextValue = e.currentTarget.value;
          setValue(nextValue);
          props.onValueChange(props.name, nextValue);
        }}
      />
    </div>
  );
};

export const MinimumReachControls: React.FC<MinimumReachControlsProps> = (
  props
) => {
  const handleValueChange = (source: Source, value: number | string) => {
    const nextValues: MinimumReachByChannelType = {
      ...props.minReachByChannelType,
      [source]: value,
    };

    props.onChange(nextValues);
  };

  return (
    <div className={`my-1 flex flex-col gap-2 ${props.className}`}>
      <p>{L.MENTIONS_QUERYBUILDER_FILTER_MIN_REACH_HELP}</p>

      <div className="flex gap-3 flex-wrap">
        {props.sourceOptions.includes("youtube") && (
          <MinimumReachControl
            id="youtube_min_reach"
            name="youtube"
            label={<SourceDisplay plural id="youtube" />}
            minReachByChannelType={props.minReachByChannelType}
            onValueChange={handleValueChange}
          />
        )}

        {props.sourceOptions.includes("instagram") && (
          <MinimumReachControl
            id="instagram_min_reach"
            name="instagram"
            label={<SourceDisplay plural id="instagram" />}
            minReachByChannelType={props.minReachByChannelType}
            onValueChange={handleValueChange}
          />
        )}

        {props.sourceOptions.includes("tiktok") && (
          <MinimumReachControl
            id="tiktok_min_reach"
            name="tiktok"
            label={<SourceDisplay plural id="tiktok" />}
            minReachByChannelType={props.minReachByChannelType}
            onValueChange={handleValueChange}
          />
        )}
        {props.sourceOptions.includes("news") && (
          <MinimumReachControl
            id="news_min_reach"
            name="news"
            label={<SourceDisplay plural id="news" />}
            minReachByChannelType={props.minReachByChannelType}
            onValueChange={handleValueChange}
          />
        )}
        {props.sourceOptions.includes("twitter") && (
          <MinimumReachControl
            id="twitter_min_reach"
            name="twitter"
            label={<SourceDisplay plural id="twitter" />}
            minReachByChannelType={props.minReachByChannelType}
            onValueChange={handleValueChange}
          />
        )}
        {props.sourceOptions.includes("facebook") && (
          <MinimumReachControl
            id="facebook_min_reach"
            name="facebook"
            label={<SourceDisplay plural id="facebook" />}
            minReachByChannelType={props.minReachByChannelType}
            onValueChange={handleValueChange}
          />
        )}
      </div>
    </div>
  );
};
